import {
  APPOINTMENT_STATUS,
  AllQueuesData,
  Appointment,
  CommunicationMetaItem,
  FOLLOWUP_MESSAGE_PURPOSE,
  PAYMENT_STATUS,
  RECEIPT_PAYMENT_MODE_ID,
  TYPES_OF_APT,
  TagRecord,
} from "@elixir/types";
import moment from "dayjs";
import { LocalAppointment } from "../apis/tryst/get-all-queues";
import { QueueConfigData } from "../apis/tryst/types";
import { AppointmentConfigs } from "../types/appointment";
import { getAgeString } from "./generic-utils";

export const WAIT_SINCE_TEXT: { [k: string]: string } = {
  CN: "Cancelled",
  CND: "Cancelled",
  CNS: "Cancelled",
  RE: "Rescheduled",
  RED: "Rescheduled",
  RES: "Rescheduled",
};

export enum FollowupMessagePurpose {
  FOLLOWUP_REMINDER = "Follow-up",
  FOLLOWUP_ACCEPTANCE_RESPONSE = "flw-yes",
  FOLLOWUP_REJECTION_RESPONSE = "flw-no",
  FOLLOWUP_RESCHEDULE_RESPONSE = "flw-res",
  FOLLOWUP_SLOT_CONFIRMATION_RESPONSE = "flw-slot-conf",
  FOLLOWUP_FEELING_BETTER_RESPONSE = "flw-feel-btr",
  FOLLOWUP_RESCHEDULE_CONFIRMATION = "flw-res-cnf",

  APPOINTMENT_REMINDER = "apt-rem",
  APPOINTMENT_REMINDER_YES_RESPONSE = "apt-rem-yes",
  APPOINTMENT_REMINDER_NO_RESPONSE = "apt-rem-no",
  APPOINTMENT_REMINDER_RESCHEDULE_RESPONSE = "apt-rem-res",
  APPOINTMENT_REMINDER_NO_LATER_DATE_RESPONSE = "apt-rem-no-later-date",
  APPOINTMENT_REMINDER_NO_FEELING_BETTER_RESPONSE = "apt-rem-no-feeling-better",
}

export type A_ACTION_TYPE =
  | "ONGOING"
  | "START_VISIT"
  | "ADD_PATIENT"
  | "APPOINTMENT_DETAILS"
  | "RE_START"
  | "PARKED_ON_OTHER_DEVICE"
  | "PARKED"
  | "ABORTED";

export const ONGOING = "ONGOING";
export const PARKED = "PARKED";
export const PARKED_ON_OTHER_DEVICE = "PARKED_ON_OTHER_DEVICE";
export const RE_START = "RE_START";
export const START_VISIT = "START_VISIT";
export const ADD_PATIENT = "ADD_PATIENT";
export const APPOINTMENT_DETAILS = "APPOINTMENT_DETAILS";
export const ABORTED = "ABORTED";

export const getAppointmentActionTextForExcel = (t: A_ACTION_TYPE): string => {
  switch (t) {
    case ONGOING:
      return "Ongoing";
    case START_VISIT:
      return "Start Visit";
    case PARKED:
      return "Parked";
    case PARKED_ON_OTHER_DEVICE:
    case RE_START:
      return t === RE_START ? "Ongoing" : "Parked";
    case ADD_PATIENT:
      return "Add Patient";
    case ABORTED:
    case APPOINTMENT_DETAILS:
      return "Appointment Details";
    default:
      return "";
  }
};

export function getAppointmentAction(a: LocalAppointment): A_ACTION_TYPE {
  if (
    a.visit_type === TYPES_OF_APT.FOLLOW_UP &&
    [
      APPOINTMENT_STATUS.INITIATED,
      APPOINTMENT_STATUS.RESERVED,
      APPOINTMENT_STATUS.BOOKED,
    ].includes(a.status)
  ) {
    return START_VISIT;
  }
  return a.status === APPOINTMENT_STATUS.BOOKED ||
    a.status === APPOINTMENT_STATUS.CHECKED_IN ||
    a.status === APPOINTMENT_STATUS.ONGOING ||
    a.status === APPOINTMENT_STATUS.PARKED
    ? a.stage === "ONGOING"
      ? a.isLocalPresnt
        ? ONGOING
        : RE_START
      : a.stage === "PARKED"
        ? a.isLocalPresnt
          ? PARKED
          : PARKED_ON_OTHER_DEVICE
        : a.patient_oid && a.patient.gen
          ? START_VISIT
          : ADD_PATIENT
    : a.status === APPOINTMENT_STATUS.ABORTED
      ? ABORTED
      : APPOINTMENT_DETAILS;
}

export function reminderStatusForExcel(
  a: Appointment & { messagePurpose?: string }
): string {
  const now = new Date();
  const reminderMessages = a?.comm_meta?.filter((item) => {
    if (a.messagePurpose) {
      return item.purpose === a.messagePurpose;
    }
    return item.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REMINDER;
  });
  const sortedMessagesNearestToNow = (reminderMessages || [])?.sort((a, b) => {
    if (!(a.schFor || a.sentAt)) {
      return 9999;
    } else if (!(b.schFor || b.sentAt)) {
      return -9999;
    }

    const messageASentTime = new Date(a.sentAt || a.schFor!);
    const messageBSentTime = new Date(b.sentAt || b.schFor!);

    const timeForMessageA = Math.abs(
      now.getTime() - messageASentTime.getTime()
    );
    const timeForMessageB = Math.abs(
      now.getTime() - messageBSentTime.getTime()
    );
    return timeForMessageA - timeForMessageB;
  });

  const sentMessageStatusWhitelist = new Set<CommunicationMetaItem["status"]>([
    "Delivered",
    "Responded",
    "Read",
  ]);

  const alreadySentMessages = sortedMessagesNearestToNow.filter(
    (a) =>
      sentMessageStatusWhitelist.has(a.status) ||
      (a.status === "Sent" &&
        a.sentAt &&
        Math.abs(new Date(a.sentAt).getTime() - now.getTime()) <= 1 * 60 * 1000)
  );

  const failedToDeliverMessages = sortedMessagesNearestToNow.filter(
    (a) =>
      a.status === "Sent" &&
      a.sentAt &&
      Math.abs(new Date(a.sentAt).getTime() - now.getTime()) > 1 * 60 * 1000
  );

  const [nearestMessageToNow] = sortedMessagesNearestToNow;

  if (
    (sortedMessagesNearestToNow?.length === 0 &&
      a.visit_type === TYPES_OF_APT.FOLLOW_UP &&
      !a.messagePurpose) ||
    nearestMessageToNow?.status === "Scheduled"
  ) {
    let reminderScheduledDate = moment(a.date)
      .subtract(1, "day")
      .set("minutes", 30)
      .set("hours", 8);

    if (Math.floor(now.getTime() / 1000) - reminderScheduledDate.unix() >= 0) {
      reminderScheduledDate = reminderScheduledDate.add(1, "day");
    }

    return `Scheduled | ${
      nearestMessageToNow?.status === "Scheduled"
        ? `${moment(nearestMessageToNow.schFor).format("DD MMM | h:mm A")}`
        : `${reminderScheduledDate.format("DD MMM")} | 8:30 AM`
    }`;
  }

  if (
    sortedMessagesNearestToNow.filter((item) => item.status !== "Scheduled")
      .length === failedToDeliverMessages.length &&
    nearestMessageToNow?.status === "Sent"
  ) {
    //all messages failed to deliver
    `Not Delivered ${
      failedToDeliverMessages.length >= 1
        ? ` - Retry count: ${failedToDeliverMessages.length
            .toString()
            .padStart(2, "0")}`
        : ""
    }`;
  }

  if (alreadySentMessages.length > 0 && !!alreadySentMessages[0].sentAt) {
    const minutesSentBefore = Math.floor(
      (now.getTime() - new Date(alreadySentMessages[0].sentAt).getTime()) /
        (1000 * 60)
    );
    const hoursSentBefore = Math.floor(minutesSentBefore / 60);

    let titleToRender = "Sent";

    if (minutesSentBefore < 60) {
      titleToRender = `Sent ${minutesSentBefore}m ago`;
    } else if (hoursSentBefore < 72) {
      titleToRender = `Sent ${hoursSentBefore}h ago`;
    }

    return `${titleToRender} - ${`Total: ${alreadySentMessages.length
      .toString()
      .padStart(2, "0")}`}`;
  }

  return "--";
}

export const getTags = (
  tagIds: TagRecord["tag_id"][],
  tags: QueueConfigData["tags"]
) => {
  const tagIdToNameMap = new Map<TagRecord["tag_id"], string>();

  tagIds.forEach((item) => {
    const currTag = tags?.find((tag) => tag.tag_id === item);
    if (currTag) {
      tagIdToNameMap.set(currTag.tag_id, currTag.label);
    }
  });

  return tagIds?.map((item) => tagIdToNameMap.get(item)).join(", ") ?? "";
};

export const getVisitType = (a: Appointment) => {
  const visitType = a["visit-type"] || "";
  const channel = a.channel;
  const service = a.service;

  const title =
    visitType === "APPOINTMENT"
      ? "Appointment"
      : visitType === "WALKIN"
        ? "Walk-in"
        : "Tele";

  return `${service ? service?.service_name : title} | ${channel}`;
};

export const getSlot = (a: Appointment) => {
  return a.visit_type === TYPES_OF_APT.FOLLOW_UP &&
    a.status !== APPOINTMENT_STATUS.BOOKED
    ? "--"
    : `${moment(a.start_time).format("hh:mm A")}`;
};

export const getFollowupVisitStatusForExcel = (a: Appointment) => {
  if (a.status === APPOINTMENT_STATUS.INITIATED) {
    return "Awaiting Response";
  } else if (a.status === APPOINTMENT_STATUS.PROVISIONAL_CANCELLED) {
    const comm =
      a?.comm_meta?.find(
        (comm) =>
          comm.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REJECTION_RESPONSE
      ) ||
      a?.comm_meta?.find(
        (comm) => comm.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REMINDER
      );

    const cancelReason = comm?.messageResponse ?? "";

    return `Not Coming${cancelReason ? ` - ${cancelReason}` : ""}`;
  } else if (
    a.status === APPOINTMENT_STATUS.RESERVED ||
    a.status === APPOINTMENT_STATUS.BOOKED
  ) {
    const comm = a.comm_meta?.find(
      (comm) =>
        comm.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_ACCEPTANCE_RESPONSE
    );

    const responseMessage =
      a.status === APPOINTMENT_STATUS.RESERVED && !!comm?.messageResponse
        ? comm.messageResponse
        : "";

    let formattedResponseMessage = "";
    if (responseMessage) {
      const [startTime, endTime, timeOfDay] = responseMessage
        .replace(/\s/g, "")
        .split("-");
      formattedResponseMessage = `Prefer to visit around ${startTime} - ${endTime} in the ${timeOfDay}`;
    }

    return `Confirmed Visit${
      formattedResponseMessage ? ` - ${formattedResponseMessage}` : ""
    }`;
  } else if (a.status === APPOINTMENT_STATUS.RESCHEDULED) {
    const comm = a.comm_meta?.find(
      (comm) =>
        comm.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_RESCHEDULE_RESPONSE
    );
    const responseMessage = comm?.messageResponse ?? "";
    return `Rescheduled${responseMessage ? ` - ${responseMessage}` : ""}`;
  }

  return "";
};

export const getVisitStatus = (a: Appointment) => {
  if (
    a.visit_type === TYPES_OF_APT.FOLLOW_UP &&
    [
      APPOINTMENT_STATUS.INITIATED,
      APPOINTMENT_STATUS.RESERVED,
      APPOINTMENT_STATUS.PROVISIONAL_CANCELLED,
      APPOINTMENT_STATUS.BOOKED,
      APPOINTMENT_STATUS.RESCHEDULED,
    ].includes(a.status)
  )
    return getFollowupVisitStatusForExcel(a);

  switch (a.status) {
    case APPOINTMENT_STATUS.COMPLETED:
      return "Completed";
    case APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION:
      return "Completed (No Prescription)";
    case APPOINTMENT_STATUS.BOOKED:
      return "Booked";
    case APPOINTMENT_STATUS.CHECKED_IN:
      return "Checked In";
    case APPOINTMENT_STATUS.ONGOING:
    case APPOINTMENT_STATUS.PARKED:
      return getAppointmentActionTextForExcel(
        getAppointmentAction(a as LocalAppointment)
      );
    case APPOINTMENT_STATUS.ABORTED:
      return "Aborted";
    case APPOINTMENT_STATUS.PAYMENT_NOT_RECEIVED:
      return "Payment Not Received";
    default:
      return WAIT_SINCE_TEXT[a.status] || "";
  }
};

export const getReminderResponse = (a: Appointment) => {
  const sentMessageStatusWhitelist = new Set<CommunicationMetaItem["status"]>([
    "Delivered",
    "Responded",
    "Read",
  ]);

  const reminderMessage = a.comm_meta?.find(
    (item) =>
      item.purpose === FOLLOWUP_MESSAGE_PURPOSE.APPOINTMENT_REMINDER &&
      (sentMessageStatusWhitelist.has(item.status) || item.status === "Sent")
  );

  if (reminderMessage) {
    const reminderMessageResponse =
      reminderMessage?.messageResponse?.toLowerCase();

    if (reminderMessageResponse && reminderMessageResponse.includes("yes")) {
      return "Confirmed Visit";
    } else if (
      reminderMessageResponse &&
      reminderMessageResponse.includes("no")
    ) {
      const notComingResponseMessage = a.comm_meta?.find(
        (item) =>
          item.purpose ===
          FOLLOWUP_MESSAGE_PURPOSE.APPOINTMENT_REMINDER_NO_RESPONSE
      )?.messageResponse;
      return `Not Coming${
        notComingResponseMessage ? ` - ${notComingResponseMessage}` : ""
      }`;
    } else if (
      reminderMessageResponse &&
      reminderMessageResponse.includes("later")
    ) {
      return `Not Coming${
        reminderMessageResponse ? ` - ${reminderMessageResponse}` : ""
      }`;
    }

    return "Awaiting Response";
  }

  return "--";
};

export const getWaitTime = (a: Appointment) => {
  if (!a.visit_checkin) {
    return "--";
  }

  const checkinTime = moment(a.visit_checkin);
  const emdTime = a.visit_end ? moment(a.visit_end) : moment();

  const minutes = emdTime.diff(checkinTime, "minutes") % 60;
  const hours = emdTime.diff(checkinTime, "hours");

  return `${hours ? `${hours}h ` : ""}${minutes}m`;
};

export const getAppointmentToExcelJson = (
  appointment: Appointment,
  queueConfig?: AppointmentConfigs
) => {
  const { tags = [] } = queueConfig || {};
  const dataItem: Record<string, string | number> = {};

  dataItem["Token"] = appointment?.token || "--";
  dataItem["Patient Name"] = appointment?.patient?.fln || "--";
  dataItem["Gender"] = appointment?.patient?.gen || "--";
  dataItem["Age"] = appointment?.patient?.dob
    ? getAgeString({ dob: appointment?.patient?.dob })
    : "--";
  dataItem["About"] =
    appointment.formData?.find((fd) => fd.key === "tag")?.value || "--";
  dataItem["UHID"] =
    appointment.formData?.find((fd) => fd.key === "uhid")?.value || "--";
  dataItem["Health ID"] = appointment?.abha?.id || "--";
  dataItem["Tags"] = getTags(appointment?.tags || [], tags);
  dataItem["date"] = moment(appointment.date).format("YYYY-MM-DD");
  dataItem["Contact"] = appointment?.patient?.mobile || "--";
  dataItem["Visit Type"] = getVisitType(appointment) || "--";
  dataItem["Slot"] = getSlot(appointment) || "--";
  dataItem["Visit Status"] = getVisitStatus(appointment) || "--";
  dataItem["Reminder Response"] = getReminderResponse(appointment) || "--";
  dataItem["Reminder Status"] = reminderStatusForExcel({
    ...appointment,
    messagePurpose: FollowupMessagePurpose.APPOINTMENT_REMINDER,
  });
  dataItem["Reminder Status"] = reminderStatusForExcel(appointment);
  dataItem["Wait Time"] = getWaitTime(appointment) || "--";
  dataItem["Payment Status"] = appointment.billStatus || "--" || "--";
  dataItem["Amount"] =
    appointment.receipts?.length > 0 ? getBillTotal(appointment) : "--";
  dataItem["Self Assessment Status"] = appointment.sa_id
    ? "Filled"
    : "Not Filled";
  dataItem["Follow Up"] = appointment?.follow_up
    ? moment(appointment.follow_up).format("Do MMM YY")
    : "--";
  return dataItem;
};

export const queueDataKeysMapSheetName: Record<keyof AllQueuesData, string> = {
  followupAppointments: "Followup Appointments",
  bookedAppointments: "Booked Appointments",
  opdAppointments: "Check-In Appointments",
  completedAppointments: "Completed Appointments",
  otherAppointments: "Other Appointments",
  requestedAppointments: "Requested Appointments",
};

function getBillTotal(appointment: Appointment): number {
  let isReceiptGeneratedForPrepaidApt = false;
  let billAmount = appointment.receipts.reduce((prev, curr) => {
    if (curr.receipt_payment?.length) {
      curr.receipt_payment?.forEach((item) => {
        if (item.paymode === RECEIPT_PAYMENT_MODE_ID.EKA_PREPAID) {
          isReceiptGeneratedForPrepaidApt = true;
        }
      });
    }
    if (
      curr.net_amount &&
      (!curr.payment_id ||
        appointment.payment_status === PAYMENT_STATUS.SUCCESS)
    ) {
      return prev + curr.net_amount;
    }

    return prev;
  }, 0);

  billAmount +=
    appointment.payment_status === PAYMENT_STATUS.SUCCESS &&
    !isReceiptGeneratedForPrepaidApt
      ? appointment?.amount || 0
      : 0;
  return billAmount;
}
